import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Gjennomganger (animerte veiledninger) per modul.
 * - `seen` hentes fra kontoen (per innlogget person, uavhengig av fullmakt/virksomhet) med localStorage som fallback.
 * - `guide` (innhold) hentes én gang fra /guide og deles med Hjelp-siden.
 */
const TourContext = createContext(null);
const LS_KEY = "controlle.tours_seen";
const SELF = { skipActAs: true, skipWorkspace: true };

const readLocal = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; } };
const writeLocal = (seen) => { try { localStorage.setItem(LS_KEY, JSON.stringify(seen || {})); } catch { /* ignore */ } };

export const TourProvider = ({ children }) => {
  const { user } = useAuth();
  const [seen, setSeen] = useState(readLocal);
  const [loaded, setLoaded] = useState(false);
  const [guide, setGuide] = useState(null);
  const [active, setActive] = useState(null); // { key, source: "auto" | "manual" }
  const guidePromise = useRef(null);

  // Sett-status fra kontoen
  useEffect(() => {
    if (!user) { setLoaded(false); return; }
    let cancelled = false;
    api.get("/me/tours", SELF).then((r) => {
      if (cancelled) return;
      const merged = { ...readLocal(), ...(r.data.seen || {}) };
      setSeen(merged); writeLocal(merged); setLoaded(true);
    }).catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ensureGuide = useCallback(() => {
    if (guide) return Promise.resolve(guide);
    if (!guidePromise.current) {
      guidePromise.current = api.get("/guide", SELF).then((r) => { setGuide(r.data); return r.data; }).catch((e) => { guidePromise.current = null; throw e; });
    }
    return guidePromise.current;
  }, [guide]);

  const markSeen = useCallback(async (key) => {
    const next = { ...seen, [key]: new Date().toISOString() };
    setSeen(next); writeLocal(next);
    try { await api.post(`/me/tours/${key}`, null, SELF); } catch { /* lokal fallback holder */ }
  }, [seen]);

  const resetTours = useCallback(async (key = "alle") => {
    const next = key === "alle" ? {} : Object.fromEntries(Object.entries(seen).filter(([k]) => k !== key));
    setSeen(next); writeLocal(next);
    try { await api.delete(`/me/tours/${key}`, SELF); } catch { /* ignore */ }
  }, [seen]);

  const startTour = useCallback((key, source = "manual") => {
    ensureGuide().then(() => setActive({ key, source })).catch(() => {});
  }, [ensureGuide]);

  const closeTour = useCallback((completed) => {
    if (active) markSeen(active.key);
    setActive(null);
    return completed;
  }, [active, markSeen]);

  /** Start automatisk hvis ikke sett. Returnerer true hvis en gjennomgang ble startet. */
  const maybeAutoStart = useCallback((key) => {
    if (!loaded || active || !user || seen[key]) return false;
    startTour(key, "auto");
    return true;
  }, [loaded, active, user, seen, startTour]);

  const value = useMemo(() => ({ seen, loaded, guide, active, startTour, closeTour, markSeen, resetTours, maybeAutoStart, ensureGuide }), [seen, loaded, guide, active, startTour, closeTour, markSeen, resetTours, maybeAutoStart, ensureGuide]);
  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour må brukes innenfor TourProvider");
  return ctx;
};
